/**
 * 66.6 — bots keep out of their teammates' line of fire (the owner: a "micro flank / slightly longer path"; always on since
 * 62.58 step 7 — decision 170 retired the switch, after 144 made it the default).
 *
 * A **lane** is the line from a bot's eye to the enemy it has in sight (its brain's target, where that enemy is now), as
 * wide as FIRE_LANE.widthPeople. Two rules, applied to a bot's intent after its brain decided:
 *   - **Hold:** a bot about to fire holds while a living teammate stands in its own lane, short of the target.
 *   - **Step aside:** a bot walking forward whose next FIRE_LANE.lookaheadSeconds of walking would put it in a teammate's
 *     lane (or that already stands in one) steps sideways out of it instead — away from the lane's line, the short detour —
 *     at its walking speed; it keeps its facing, so its own aim is untouched.
 * Pure: state and the brains' targets in, an intent out. The lanes read only what each bot's brain already holds (its own
 * target in sight), so no bot learns anything new about the enemy.
 */
import type { GameState, PlayerState, UnitIntent, Vec3 } from '../types';
import { PERSON_BODY_HEIGHT } from '../constants';
import { vecAdd, vecDot, vecLength, vecNormalize, vecScale, vecSub } from '../math';
import { hitboxCenter } from '../core/bullets';
import { personEyePoint } from '../core/fineTerrain';
import { personSetOf } from '../core/scaleProfile';
import { headingOf, obstaclesNear, rightOf } from '../core/surfaceWalk';
import { footprintObstacles, personMover } from '../core/hits';

export const FIRE_LANE = { widthPeople: 1.2, lookaheadSeconds: 0.6 } as const;

/** A bot's line of fire: its eye to its target in sight. `geo` (62.57 cut 2): its direction, squared length and length,
 * worked out once with the lane — every test of it used to work them out again (69 million tests in 110 s at 50 a side). */
export interface Lane { owner: number; from: Vec3; to: Vec3; geo?: { dx: number; dy: number; dz: number; len2: number; len: number } }
function geoOf(lane: Lane): NonNullable<Lane['geo']> {
  if (lane.geo) return lane.geo;
  const dx = lane.to.x - lane.from.x, dy = lane.to.y - lane.from.y, dz = lane.to.z - lane.from.z;
  const len2 = dx * dx + dy * dy + dz * dz;
  return (lane.geo = { dx, dy, dz, len2, len: Math.sqrt(len2) });
}

/** What a lane needs from a bot's brain: the enemy it has in sight, if any. */
export interface LaneBrain { targetId: number | null; visible: boolean }

/** Is point p within `width` of the lane's line, between its ends (not behind the shooter, not past the target)? */
export function inLane(p: Vec3, lane: Lane, width: number): boolean {
  // (62.57 cut 2: no vectors made — the same arithmetic, in the same order, as vecSub / vecDot / vecAdd / vecScale)
  const { dx, dy, dz, len2 } = geoOf(lane);
  if (!(len2 > 0)) return false;
  const rx = p.x - lane.from.x, ry = p.y - lane.from.y, rz = p.z - lane.from.z;
  const t = (rx * dx + ry * dy + rz * dz) / len2;
  if (t <= 0.02 || t >= 0.98) return false;
  const ox = p.x - (lane.from.x + dx * t), oy = p.y - (lane.from.y + dy * t), oz = p.z - (lane.from.z + dz * t);
  return Math.sqrt(ox * ox + oy * oy + oz * oz) < width;
}

/**
 * The tick's points (lane F's scale sweep, Oct 11: fire lanes were ×2–3 tick time, every bot rebuilding every teammate's
 * lane — two fine-ground lookups each — and every hold reading every teammate's hit box). Within one tick nobody moves
 * while the bots decide, so each unit's eye and hit-box centre is worked out once and shared, and the team lists are
 * sorted once; a lane is still assembled from its owner's brain as it stands (exactly as before — bit-identical). Keyed on
 * the tick and the players array; without a tick (a test, a one-off call) nothing is kept.
 */
interface TickPoints { tick: number; players: readonly PlayerState[]; eye: Map<number, Vec3>; body: Map<number, Vec3>; mates: Map<number, PlayerState[]>; byId: Map<number, PlayerState>; lanes: Map<number, Lane> }
let tickPoints: TickPoints | null = null;
function pointsFor(state: GameState, tick: number | undefined): TickPoints | null {
  if (tick === undefined) return null;
  if (!tickPoints || tickPoints.tick !== tick || tickPoints.players !== state.players) {
    tickPoints = { tick, players: state.players, eye: new Map(), body: new Map(), mates: new Map(), byId: new Map(state.players.map(p => [p.id, p])), lanes: new Map() };
  }
  return tickPoints;
}

/** Work done, for the cost measures: lanes assembled, players looked up by id, lane tests made (never read by the game). */
export const fireLaneWork = { lanes: 0, finds: 0, tests: 0 };
export function resetFireLaneWork(): void { fireLaneWork.lanes = 0; fireLaneWork.finds = 0; fireLaneWork.tests = 0; }

/**
 * Could point `p` be in `lane` (width `width`)? Exact and cheap: a point in a lane lies within the lane's length and its
 * width of the shooter's eye, so anything farther is out without the lane test (lane F's spread-vs-dense sweep: every
 * teammate pair, at any range, was tested). The lane test itself decides the rest, as before.
 */
function nearLane(p: Vec3, lane: Lane, width: number): boolean {
  const dx = p.x - lane.from.x, dy = p.y - lane.from.y, dz = p.z - lane.from.z;
  const reach = geoOf(lane).len + width;
  return dx * dx + dy * dy + dz * dz <= reach * reach;
}
export function inLaneNear(p: Vec3, lane: Lane, width: number): boolean {
  if (!nearLane(p, lane, width)) return false;
  fireLaneWork.tests++;
  return inLane(p, lane, width);
}
function eyeOf(state: GameState, p: PlayerState, pts: TickPoints | null): Vec3 {
  const hit = pts?.eye.get(p.id);
  if (hit) return hit;
  const ps = personSetOf(state), v = personEyePoint(p, state.planets, ps, ps.losEyeHeight);
  pts?.eye.set(p.id, v);
  return v;
}
function bodyOf(state: GameState, p: PlayerState, pts: TickPoints | null): Vec3 {
  const hit = pts?.body.get(p.id);
  if (hit) return hit;
  const ps = personSetOf(state), v = hitboxCenter(p, state.planets, ps.hitboxCenter, ps);
  pts?.body.set(p.id, v);
  return v;
}
/** The living players of a team on a planet, in id order (once a tick). */
function teamOn(state: GameState, team: number, planetIndex: number, pts: TickPoints | null): PlayerState[] {
  const key = team * 4096 + planetIndex;   // (62.60 lever 1: a number, not a string a call)
  const hit = pts?.mates.get(key);
  if (hit) return hit;
  const list = [...state.players].sort((a, b) => a.id - b.id).filter(m => m.alive && m.teamIndex === team && m.planetIndex === planetIndex);
  pts?.mates.set(key, list);
  return list;
}

/** A bot's lane, when its brain has an enemy in sight (alive, on its planet). `tick` shares the tick's points. */
export function laneOf(state: GameState, unit: PlayerState, brain: LaneBrain | undefined, tick?: number): Lane | null {
  if (!brain || !brain.visible || brain.targetId === null) return null;
  const pts = pointsFor(state, tick);
  fireLaneWork.finds++;
  const e = pts ? pts.byId.get(brain.targetId) : state.players.find(p => p.id === brain.targetId);
  if (!e || !e.alive || e.planetIndex !== unit.planetIndex) return null;
  // (a lane's ends are its owner's eye and its target's hit box this tick — positions only, so one lane serves every bot
  // that reads it; whether it stands is its owner's brain as it stands, checked above every time)
  const key = unit.id * 2097152 + e.id;   // (62.60 lever 1: a number, not a string a call; ids stay under 2^21)
  const hit = pts?.lanes.get(key);
  if (hit) return hit;
  fireLaneWork.lanes++;
  const lane = { owner: unit.id, from: eyeOf(state, unit, pts), to: bodyOf(state, e, pts) };
  pts?.lanes.set(key, lane);
  return lane;
}

/** The lanes of `unit`'s living teammates on its planet (in id order), from their brains. */
export function teammateLanes(state: GameState, unit: PlayerState, brainOf: (id: number) => LaneBrain | undefined, tick?: number): Lane[] {
  const out: Lane[] = [];
  for (const m of teamOn(state, unit.teamIndex, unit.planetIndex, pointsFor(state, tick))) {
    if (m.id === unit.id) continue;
    const lane = laneOf(state, m, brainOf(m.id), tick);
    if (lane) out.push(lane);
  }
  return out;
}

/**
 * 62.57 cut 2: a team's lanes this tick, shared — each living member's lane (or none), in id order, built once a tick
 * (it replaced each bot rebuilding every teammate's lane: 57 million lookups in 110 s at 50 a side). §5.16 step 3: built
 * from the start-of-tick brains and never changed within the tick (cut 2's refresh after each decision is gone), so every
 * bot reads the same list whatever order the bots decide in — what `teammateLanes` assembles over the start-of-tick brains.
 */
export interface TeamLanes { tick: number; players: readonly PlayerState[]; byTeam: Map<number, { ids: number[]; lanes: Array<Lane | null> }> }
export function teamLanesFor(cache: TeamLanes | null, state: GameState, unit: PlayerState, brainOf: (id: number) => LaneBrain | undefined, tick: number): { cache: TeamLanes; entry: { ids: number[]; lanes: Array<Lane | null> } } {
  const c = cache && cache.tick === tick && cache.players === state.players ? cache : { tick, players: state.players, byTeam: new Map() };
  const key = unit.teamIndex * 4096 + unit.planetIndex;
  let entry = c.byTeam.get(key);
  if (!entry) {
    const team = teamOn(state, unit.teamIndex, unit.planetIndex, pointsFor(state, tick));
    entry = { ids: team.map(m => m.id), lanes: team.map(m => laneOf(state, m, brainOf(m.id), tick)) };
    c.byTeam.set(key, entry);
  }
  return { cache: c, entry };
}
/** The lanes of `unit`'s teammates from the shared list (its own left out), as `teammateLanes` returns them. */
export function matesFrom(entry: { ids: number[]; lanes: Array<Lane | null> }, unitId: number): Lane[] {
  const out: Lane[] = [];
  for (let k = 0; k < entry.ids.length; k++) { const l = entry.lanes[k]; if (l && entry.ids[k] !== unitId) out.push(l); }
  return out;
}

/** What the rules did, for the harness's functional check (counts only; never read by the game). */
export const fireLaneStats = { holds: 0, steps: 0 };

/** Is a short step along `dir` (a unit tangent) from `unit` clear of the walk's obstacles (their footprints, its body)? */
const SIDE_STEP_LOOK = 1.5;   // body radii ahead, past touching
export function sideClear(state: GameState, unit: PlayerState, dir: Vec3, ps: ReturnType<typeof personSetOf>): boolean {
  const planet = state.planets[unit.planetIndex];
  const n = vecNormalize(unit.surfaceDir), r = ps.unitRadius;
  const ahead = vecNormalize(vecAdd(n, vecScale(dir, (SIDE_STEP_LOOK * r) / planet.radius)));
  const fp = footprintObstacles(state, unit.planetIndex, personMover(ps));
  for (const o of obstaclesNear(fp, ahead, (fp.maxRadius + 2 * r) / planet.radius)) {
    if (vecLength(vecSub(vecNormalize(o.dir), ahead)) * planet.radius < o.radius + r) return false;
  }
  return true;
}

/** The bot's intent with the two rules applied (see the header). */
export function keepOutOfLanes(state: GameState, unit: PlayerState, intent: UnitIntent, own: Lane | null, mates: ReadonlyArray<Lane | null>, tick?: number, takingCover = false): UnitIntent {
  // (62.57 cut 2: `mates` may be the team's shared list as it stands — its empty entries and the bot's own skipped here, in
  // the same id order as the per-bot list, so no list is made for each bot)
  const mate = (l: Lane | null): l is Lane => !!l && l.owner !== unit.id;
  if (unit.airborne || unit.mount) return intent;
  const ps = personSetOf(state);
  const pts = pointsFor(state, tick);
  const width = FIRE_LANE.widthPeople * PERSON_BODY_HEIGHT * ps.k;
  let out = intent;
  // hold: a teammate in my own lane, short of the target
  if (out.fire && own) {
    // (62.57 cut 2: the tick's list of the living team on this planet, not every player filtered again — the same answer)
    const blocking = teamOn(state, unit.teamIndex, unit.planetIndex, pts).some(m => m.id !== unit.id && inLaneNear(bodyOf(state, m, pts), own, width));
    if (blocking) { out = { ...out, fire: false }; fireLaneStats.holds++; }
  }
  // step aside: walking into (or standing in) a teammate's lane — never while it is held still by its own act (a bandage,
  // treating a teammate, planting a beacon, launching or flying a drone): a step cancels it (588 bandages stopped against
  // 68, the every-switch check of Oct 11)
  // — nor while it is going to cover or holding it (`takingCover`: hurt and making for a spot behind something; stepped
  // aside it turned off its way again and again short of the spot — 68.16's measure fell 30 % → 14 % once the lanes were on
  // by default). Its own hold still applies.
  const busy = takingCover || unit.healLeft !== undefined || unit.treating !== undefined || unit.plantLeft !== undefined || (unit.droneLaunchLeft ?? 0) > 0 || !!unit.droneTasking;
  // (62.57 cut 2: the body's test once a lane, kept for the step's own look below)
  const bodyIn: Array<boolean | undefined> = [];
  const bodyInLane = (k: number): boolean => bodyIn[k] ?? (bodyIn[k] = inLaneNear(bodyOf(state, unit, pts), mates[k]!, width));
  if (!busy && mates.some(mate) && (out.forward > 0 || out.right !== 0 || mates.some((l, k) => mate(l) && bodyInLane(k)))) {
    const body = bodyOf(state, unit, pts);
    const n = vecNormalize(unit.surfaceDir);
    const heading = headingOf(unit), right = rightOf(heading, n);
    const move = vecAdd(vecScale(heading, out.forward), vecScale(right, out.right));
    const ahead = vecAdd(body, vecScale(move, ps.walkSpeed * FIRE_LANE.lookaheadSeconds));
    const lane = mates.find((l, k): l is Lane => mate(l) && (inLaneNear(ahead, l, width) || bodyInLane(k)));
    if (lane) {
      // the side of the lane's line the bot is on, by its right vector: step that way, out of the lane
      const d = vecNormalize(vecSub(lane.to, lane.from));
      const rel = vecSub(body, lane.from);
      const off = vecSub(rel, vecScale(d, vecDot(rel, d)));
      const away = vecDot(off, right) >= 0 ? 1 : -1;
      // a side step the walk's look-ahead steer never sees (it comes after the brain): take it only to a side clear of
      // anything within a short step — away from the lane's line first, else the other way, else hold where it is (the
      // lanes walked bots sideways into trunks: the steer test's stuck hops 1 with the lanes off, 7 with them on). Both sides
      // blocked, it walks on as its brain meant (holding still there, the brain's stuck check would take it for a wall and hop)
      const side = [away, -away].find(sgn => sideClear(state, unit, vecScale(right, sgn), ps));
      if (side !== undefined) { out = { ...out, forward: 0, right: side }; fireLaneStats.steps++; }
    }
  }
  return out;
}
